import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isListEnabled } from 'utils/queryHelpers';

import { importTaskService } from 'services';
import { QueryContextFromKeys, QueryParamModel } from 'models';

type ImportTaskQueryContexts = QueryContextFromKeys<typeof importTaskKeys>;

export const importTaskKeys = {
  all: [{ scope: 'importTask' }] as const,
  lists: () => [{ ...importTaskKeys.all[0], entity: 'list' }] as const,
  list: (params?: QueryParamModel | null) => [{ ...importTaskKeys.lists()[0], params }] as const,
  task: (id: ID) => [{ ...importTaskKeys.lists()[0], id }] as const,
};

const fetchTaskList = async ({ queryKey: [{ params }] }: ImportTaskQueryContexts['list']) =>
  importTaskService.getTasks(params);

const fetchTaskById = async ({ queryKey: [{ id }] }: ImportTaskQueryContexts['task']) =>
  importTaskService.getTaskById(id);

export const useTaskList = (params?: QueryParamModel | null) =>
  useQuery({
    queryKey: importTaskKeys.list(params),
    queryFn: fetchTaskList,
    enabled: isListEnabled(params),
  });

export const useTask = (id: ID) =>
  useQuery({ queryKey: importTaskKeys.task(id), queryFn: fetchTaskById });

export const useImportTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => importTaskService.upload(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importTaskKeys.lists() });
    },
  });
};

export const useRetryImportTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => importTaskService.retryUpload(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importTaskKeys.lists() });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: ID) => importTaskService.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importTaskKeys.lists() });
    },
  });
};
